import { format } from 'date-fns';
import React, { useState } from 'react';

import ContactDetailModal from '@/shared/components/Common/ContactDetailModal';
import { useContactMutations } from '@/Users/hooks/useContactMutations';
import { Contact, ContactListProps } from '@/Users/types/userTypes';

const ContactList: React.FC<ContactListProps> = ({ contactList, userUuid }) => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [selectedContactId, setSelectedContactId] = useState<number | null>(
    null,
  );
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);

  const contactMutations = useContactMutations(
    selectedContact?.id ?? 0,
    userUuid,
  );

  const handleContactClick = (contact: Contact) => {
    setSelectedContact(contact);
    setSelectedContactId(contact.id);
    setIsContactModalOpen(true);

    if (contact.status.toUpperCase() === 'UNREAD') {
      setTimeout(() => {
        if (contactMutations) {
          contactMutations.updateStatus('READ');
        }
      }, 100);
    }
  };

  const handleCloseModal = () => {
    setIsContactModalOpen(false);
    setSelectedContactId(null);
    setSelectedContact(null);
  };

  const getStatusColor = (status: string) => {
    switch (status.toUpperCase()) {
      case 'READ':
        return 'bg-[#7676E9]';
      case 'UNREAD':
        return 'bg-[#BBBBBB]';
      default:
        return 'bg-[#888]';
    }
  };

  return (
    <section
      className="rounded border border-[#f1f1f1] bg-white shadow-sm"
      aria-label="Contact list"
    >
      <header className="border-b-[1.5px] border-[#eaeaea] p-5">
        <h2
          className="text-sm font-medium text-[#1d1d1d]"
          id="contact-list-title"
        >
          Contact
        </h2>
      </header>

      <ul
        className="flex w-full flex-col items-center space-y-2 px-6 py-4"
        aria-labelledby="contact-list-title"
      >
        {contactList.length > 0 ? (
          contactList.map((contact) => (
            <li key={contact.id} className="w-full">
              <div
                role="button"
                tabIndex={0}
                aria-label="View contact detail"
                className="w-full cursor-pointer"
                onClick={() => handleContactClick(contact)}
              >
                <div className="w-full border border-[#f1f1f1] p-3">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-normal text-[#888]">
                      {format(new Date(contact.createdAt), 'yyyy.MM.dd HH:mm')}
                    </p>
                    <span
                      className={`inline-block rounded-full px-4 py-1 text-center text-xs font-semibold tracking-wide text-white ${getStatusColor(contact.status)}`}
                      aria-label={`Status: ${contact.status}`}
                    >
                      {contact.status}
                    </span>
                  </div>
                  <p className="mt-3 text-sm leading-[160%] text-[#1d1d1d]">
                    {contact.content.length > 100
                      ? `${contact.content.substring(0, 100)}...`
                      : contact.content}
                  </p>
                </div>
                {contact.memo && (
                  <div className="w-full rounded-b border-b border-l border-r border-[#f1f1f1] bg-[#f6f6f6] px-4 py-3 text-xs text-[#666]">
                    Memo:{' '}
                    {contact.memo.length > 50
                      ? `${contact.memo.substring(0, 50)}...`
                      : contact.memo}
                  </div>
                )}
              </div>
            </li>
          ))
        ) : (
          <li className="w-full">
            <div className="mt-4 p-5 text-center text-sm text-[#666]">
              No contacts found
            </div>
          </li>
        )}
      </ul>

      {isContactModalOpen && selectedContactId && (
        <ContactDetailModal
          contactId={selectedContactId}
          onClose={handleCloseModal}
          userUuid={userUuid}
        />
      )}
    </section>
  );
};

export default ContactList;
